import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface Company {
  id: string;
  name: string;
  tenant_id: string;
  customer_count: number;
}

interface ImportStatus {
  [companyId: string]: {
    status: 'idle' | 'importing' | 'success' | 'error';
    message?: string;
  };
}

export default function CustomerImportTriggerPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [importStatus, setImportStatus] = useState<ImportStatus>({});

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);

      // Get companies with customer counts
      const { data: companiesData } = await supabase
        .from('sw_companies')
        .select('id, name, tenant_id')
        .eq('is_active', true)
        .order('name');

      if (companiesData) {
        // Get customer counts for each company
        const companiesWithCounts = await Promise.all(
          companiesData.map(async (company) => {
            const { count } = await supabase
              .from('customers')
              .select('*', { count: 'exact', head: true })
              .eq('created_by_company_id', company.id)
              .eq('data_source', 'successware');

            return {
              ...company,
              customer_count: count || 0
            };
          })
        );

        setCompanies(companiesWithCounts);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerImport = async (companyId: string, companyName: string) => {
    try {
      setImportStatus(prev => ({
        ...prev,
        [companyId]: { status: 'importing', message: 'Starting import...' }
      }));

      // Call the successware-import edge function
      const { data, error } = await supabase.functions.invoke('successware-import', {
        body: {
          company_id: companyId,
          entity: 'customers'
        }
      });

      if (error) throw error;

      setImportStatus(prev => ({
        ...prev,
        [companyId]: { 
          status: 'success', 
          message: `Import completed! Imported ${data?.imported || 0} customers.` 
        }
      }));

      // Reload companies to update counts
      setTimeout(() => loadCompanies(), 2000);

    } catch (error: any) {
      console.error('Import error:', error);
      setImportStatus(prev => ({
        ...prev,
        [companyId]: { 
          status: 'error', 
          message: error.message || 'Import failed. Check console for details.' 
        }
      }));
    }
  };

  const getStatusColor = (status?: 'idle' | 'importing' | 'success' | 'error') => {
    switch (status) {
      case 'importing': return 'bg-blue-100 text-blue-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return '';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Customer Import Trigger</h1>
        <p className="text-gray-600 mt-1">
          Manually trigger customer imports from SuccessWare for each company
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Safe to re-run:</strong> The import uses upsert - already imported customers will be skipped, only new/updated customers will be imported. No duplicates will be created.
            </p>
          </div>
        </div>
      </div>

      {/* Companies List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading companies...</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Customers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {companies.map((company) => {
                const status = importStatus[company.id];
                return (
                  <tr key={company.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{company.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{company.tenant_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{company.customer_count.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {status && (
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
                          {status.status === 'importing' && (
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          {status.message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => triggerImport(company.id, company.name)}
                        disabled={status?.status === 'importing'}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {status?.status === 'importing' ? 'Importing...' : 'Import Customers'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Expected Totals */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Expected Totals</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Climate Control Services: ~20,000 customers</li>
          <li>• Abraham AC: ~9,800 customers</li>
          <li>• Hales AC: ~20,000 customers</li>
          <li className="font-medium text-gray-900 mt-2">• Total Expected: ~50,000 customers</li>
        </ul>
      </div>
    </div>
  );
}
